import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { HlmIconComponent } from '../../ui/icon.component';
import { HlmTooltipDirective } from '../../ui';
import { PermissionService } from '../../core/auth/permission.service';
import { AreaStateService } from '../../core/area-state/area-state.service';
import { NAV_AREAS, NavArea, resolveActiveAreaId } from '../../core/navigation/nav-area.model';

/**
 * EB-4038 — AMIE area switcher (activity bar).
 *
 * Permanent far-left icon-rail. One icon per product area (CMT / Brand Manager /
 * Call Viewer). A pure switcher: each area keeps its own internal navigation.
 * Active area derived from the router URL; areas the user cannot access are hidden.
 */
@Component({
  selector: 'cmt-nav-rail',
  standalone: true,
  imports: [HlmIconComponent, HlmTooltipDirective],
  template: `
    <nav
      aria-label="Areas"
      class="z-30 h-full w-[76px] border-r border-border bg-card"
    >
      <!-- Inner sticky track: <nav> stretches to the row height (no forced -->
      <!-- viewport calc → no overflow); the icons stick below the header. -->
      <div class="sticky top-14 flex max-h-[calc(100vh-5.5rem)] flex-col items-center gap-2 py-4">
      @for (area of visibleAreas(); track area.id) {
        <button
          type="button"
          (click)="select(area)"
          [attr.aria-label]="area.label"
          [attr.aria-current]="area.id === activeAreaId() ? 'page' : null"
          [hlmTooltip]="area.label"
          position="right"
          class="relative flex w-16 flex-col items-center gap-1 rounded-lg py-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          [class]="area.id === activeAreaId() ? 'bg-accent/15 text-accent' : 'text-muted-foreground hover:text-foreground'"
        >
          @if (area.id === activeAreaId()) {
            <span
              aria-hidden="true"
              class="absolute left-0 top-1/2 h-9 w-0.5 -translate-y-1/2 rounded-r bg-accent"
            ></span>
          }
          <hlm-icon [name]="area.icon" size="1.3rem" />
          <span class="text-sm font-medium leading-none">{{ area.shortLabel }}</span>
        </button>
      }
      </div>
    </nav>
  `,
})
export class NavRailComponent {
  private readonly router = inject(Router);
  private readonly permissionService = inject(PermissionService);
  private readonly areaStateService = inject(AreaStateService);

  // Router URL is not a signal — bridge NavigationEnd into a signal.
  private readonly urlSignal = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly activeAreaId = computed(() => resolveActiveAreaId(this.urlSignal()));

  readonly visibleAreas = computed(() =>
    NAV_AREAS.filter((a) => !a.permissionKey || this.permissionService.canRead(a.permissionKey)),
  );

  /**
   * Pure area switch — does NOT clear search (AD-9). Restores the last URL the
   * user was on in that area (deep link / filters), falling back to the area
   * root on first visit. Clicking the already-active area is a no-op so it can't
   * accidentally discard the current deep-link state.
   */
  select(area: NavArea): void {
    if (area.id === this.activeAreaId()) return;
    this.router.navigateByUrl(this.areaStateService.lastUrl(area.id) ?? area.route);
  }
}
